import { toast } from 'react-toastify'

/**
 * Simple EventEmitter implementation for modal communication
 * Allows components to subscribe to modal events without direct coupling
 */
class EventEmitter {
  constructor() {
    this.events = {}
  }

  /**
   * Subscribe to an event
   * @param {string} event - Event name
   * @param {Function} callback - Callback function
   * @returns {Function} Unsubscribe function
   */
  on(event, callback) {
    if (!this.events[event]) {
      this.events[event] = []
    }

    // Prevent duplicate listeners
    if (this.events[event].includes(callback)) {
      console.warn(`[EventEmitter] Duplicate listener for event: ${event}. Returning existing unsubscribe function.`)
      return () => {
        if (this.events[event]) {
          this.events[event] = this.events[event].filter((cb) => cb !== callback)
        }
      }
    }

    this.events[event].push(callback)

    // Return unsubscribe function
    return () => {
      if (this.events[event]) {
        this.events[event] = this.events[event].filter((cb) => cb !== callback)
      }
    }
  }

  /**
   * Emit an event to all subscribers
   * @param {string} event - Event name
   * @param {*} data - Event data
   */
  emit(event, data) {
    if (!this.events[event]) return

    this.events[event].forEach((callback) => {
      try {
        callback(data)
      } catch (error) {
        console.error(`Error in event handler for ${event}:`, error)
      }
    })
  }
}

// Create singleton instance for modal events
const modalEmitter = new EventEmitter()

/**
 * Show quota exceeded modal and toast notification
 *
 * @param {Object} data - Quota exceeded data from API
 * @param {string} data.quota_type - Type of quota exceeded ('stories' or 'regenerations')
 * @param {number} data.used - Current usage
 * @param {number} data.limit - Quota limit
 * @param {boolean} data.unlimited - Whether quota is unlimited
 * @param {string} data.resets_at - ISO date when quota resets
 * @param {string} data.message - Error message from API
 *
 * @example
 * // In API interceptor
 * showQuotaExceededModal({
 *   quota_type: 'stories',
 *   used: 100,
 *   limit: 100,
 *   resets_at: '2025-11-01T00:00:00Z',
 *   message: 'You have reached your stories limit'
 * })
 */
export function showQuotaExceededModal(data) {
  // Emit event for modal listeners (modal shows all quota info)
  modalEmitter.emit('quota-exceeded', data)
}

/**
 * Show feature locked modal and toast notification
 *
 * @param {Object} data - Feature locked data from API
 * @param {string} data.feature_id - Feature identifier (e.g., 'video_upload')
 * @param {string} data.message - Error message from API
 *
 * @example
 * // In API interceptor
 * showFeatureLockedModal({
 *   feature_id: 'video_upload',
 *   message: 'Video upload is not available in your plan'
 * })
 */
export function showFeatureLockedModal(data) {
  // Emit event for modal listeners
  modalEmitter.emit('feature-locked', data)

  // Show toast notification
  const featureLabel = formatFeatureName(data.feature_id)

  toast.warning(
    data.message || `${featureLabel} is not available in your current plan. Upgrade to unlock this feature.`,
    {
      toastId: `feature-locked-${data.feature_id}`, // Prevent duplicate toasts
      position: 'top-right',
      autoClose: 6000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
    }
  )
}

/**
 * Subscribe to quota exceeded events
 *
 * @param {Function} callback - Callback function to handle quota exceeded events
 * @returns {Function} Unsubscribe function
 *
 * @example
 * // In a React component
 * useEffect(() => {
 *   const unsubscribe = onQuotaExceeded((data) => {
 *     setShowUpgradeModal(true)
 *     setQuotaData(data)
 *   })
 *   return unsubscribe
 * }, [])
 */
export function onQuotaExceeded(callback) {
  return modalEmitter.on('quota-exceeded', callback)
}

/**
 * Subscribe to feature locked events
 *
 * @param {Function} callback - Callback function to handle feature locked events
 * @returns {Function} Unsubscribe function
 *
 * @example
 * // In a React component
 * useEffect(() => {
 *   const unsubscribe = onFeatureLocked((data) => {
 *     setShowUpgradeModal(true)
 *     setFeatureData(data)
 *   })
 *   return unsubscribe
 * }, [])
 */
export function onFeatureLocked(callback) {
  return modalEmitter.on('feature-locked', callback)
}

/**
 * Helper function to format feature IDs into readable names
 *
 * @param {string} featureId - Feature identifier
 * @returns {string} Formatted feature name
 * @private
 */
function formatFeatureName(featureId) {
  const featureNames = {
    video_upload: 'Video Upload',
    advanced_analytics: 'Advanced Analytics',
    posting_interval_1h: '1 Hour Posting Interval',
    custom_branding: 'Custom Branding',
  }

  return featureNames[featureId] || featureId
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase())
}
